import React, { Suspense, lazy, useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SocketProvider } from "./contexts/SocketContext";
import { SupportModalProvider } from './contexts/SupportModalContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ErrorBoundary from './components/Shared/ErrorBoundary';
import InstallPWA from './components/Shared/InstallPWA';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import OfflineBanner from './components/Shared/OfflineBanner';
import AppLanding from './components/Shared/AppLanding';
import ForceUpdateModal from './components/Shared/ForceUpdateModal';
import { useForceUpdate } from './hooks/useForceUpdate';

// Lazy-loaded routes for code splitting
const Home = lazy(() => import('./components/Auth/Home'));
const Login = lazy(() => import('./components/Auth/Login'));
const LoginAdmin = lazy(() => import('./components/Auth/LoginAdmin'));
const ForgetPassword = lazy(() => import('./components/Auth/ForgetPassword'));
const ResetPassword = lazy(() => import('./components/Auth/ResetPassword'));
const Otp = lazy(() => import('./components/Auth/Otp'));
const PrivacyPolicy = lazy(() => import('./components/Shared/PrivacyPolicy'));
const DeleteAccount = lazy(() => import('./components/Shared/DeleteUser'));
const TermsAndConditions = lazy(() => import('./components/Shared/TermsConditions'));
const ErrorPage = lazy(() => import('./components/ErrorPages/error'));
const SuperAdminDashboard = lazy(() => import('./components/Dashboard/SuperAdminDashboard'));
const AdminDashboard = lazy(() => import('./components/Dashboard/AdminDashboard'));
const ServiceAgentDashboard = lazy(() => import('./components/Dashboard/ServiceAgentDashboard'));
const UserDashboard = lazy(() => import('./components/Dashboard/UserDashboard'));

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
    <div className="flex flex-col items-center gap-3">
      <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
      <p className="text-gray-500 text-sm">Loading...</p>
    </div>
  </div>
);

// Capacitor init — runs once on native platforms
async function initCapacitor() {
  try {
    const { SplashScreen } = await import('@capacitor/splash-screen');
    const { StatusBar, Style } = await import('@capacitor/status-bar');
    const { App: CapApp } = await import('@capacitor/app');

    await SplashScreen.hide({ fadeOutDuration: 500 });
    await StatusBar.setStyle({ style: Style.Light });
    await StatusBar.setBackgroundColor({ color: '#0d9488' });

    // Handle Android hardware back button
    CapApp.addListener('backButton', ({ canGoBack }) => {
      if (!canGoBack) CapApp.exitApp();
      else window.history.back();
    });
  } catch {
    // Not running in Capacitor — browser env, safe to ignore
  }
}

function AppWrapper() {
  const [onboardingDone, setOnboardingDone] = useState(
    () => localStorage.getItem('ml_onboarding_done') === '1'
  );

  useEffect(() => {
    // Init Capacitor plugins if running as native app
    if (window.Capacitor?.isNativePlatform?.()) {
      initCapacitor();
    }
  }, []);

  const handleOnboardingDone = () => {
    localStorage.setItem('ml_onboarding_done', '1');
    setOnboardingDone(true);
  };

  // Show onboarding/landing only on first launch (native or PWA)
  if (!onboardingDone) {
    return (
      <Router>
        <AppLanding onDone={handleOnboardingDone} />
      </Router>
    );
  }

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <LanguageProvider>
          <AuthProvider>
            <AppWithUpdateCheck />
          </AuthProvider>
        </LanguageProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

function AppWithUpdateCheck() {
  const { status, latestVersion, updateMessage, downloadUrl, dismiss } = useForceUpdate();

  return (
    <>
      {/* Force update blocks entire UI; soft update shows dismissible banner */}
      <ForceUpdateModal
        status={status}
        latestVersion={latestVersion}
        updateMessage={updateMessage}
        downloadUrl={downloadUrl}
        dismiss={dismiss}
      />
      {/* Only render main app when NOT force-blocked */}
      {status !== 'force-update' && <App />}
    </>
  );
}

function App() {
  const { user, admin, loading } = useAuth();

  if (loading) return <PageLoader />;

  return (
    <SocketProvider>
      <SupportModalProvider>
        <Router>
          <div className="App">
            <OfflineBanner />
            <ToastContainer position="top-right" autoClose={3000} />
            <InstallPWA />
            <Suspense fallback={<PageLoader />}>
              <Routes>
              {/* Public Routes */}
              <Route path="/home" element={<Home />} />
              <Route path="/privacypolicy" element={<PrivacyPolicy/>} />
              <Route path="/deletemyaccount" element={<DeleteAccount/>}/>
              <Route path="/terms" element={<TermsAndConditions/>}/> 
              <Route path="/login" element={<Login />} />
              <Route path="/loginadmin" element={<LoginAdmin />} />
              <Route path="/error" element={<ErrorPage />} />
              <Route path="/forgetpassword" element={<ForgetPassword />} />
              <Route path="/resetpassword" element={<ResetPassword />} />

              {/* Protected Routes */}
              <Route
                path="/superadmin/*"
                element={
                  <ProtectedRoute requiredRole="super_admin">
                    <SuperAdminDashboard />
                  </ProtectedRoute>
                }
              />
              <Route 
                path="/admin" 
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AdminDashboard />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/agent" 
                element={
                  <ProtectedRoute requiredRole="service_agent">
                    <ServiceAgentDashboard />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/dashboard" 
                element={
                  <ProtectedRoute requiredRole="user">
                    <UserDashboard />
                  </ProtectedRoute>
                } 
              />
              <Route
                path="/otp"
                element={
                  <ProtectedRoute requiredRole="user">
                    <Otp />
                  </ProtectedRoute>
                } 
              />

              <Route path="*" element={<Navigate to="/home" replace />} />
            </Routes>
            </Suspense>
            <InstallPWA />
          </div>
        </Router>
      </SupportModalProvider>
    </SocketProvider>
  );
}

export default AppWrapper;
